import { Component } from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { firstValueFrom } from "rxjs";
import { TypeModule } from "src/app/shared/enums/type-module";
import { IEvent, IEventUser, IModule, ISchedule } from "src/app/shared/interfaces";
import { IAppointment } from "src/app/shared/interfaces/appointments.interfaces";
import { getSpecificLocation } from "src/app/shared/selectors/generics-modules-data.selectors";
import { AppointmentsService, SchedulesService } from "src/app/shared/services";
import { TranslateService } from "@ngx-translate/core";
import { CapacitorCalendar } from "@ebarooni/capacitor-calendar";

@Component({
	selector: "app-confirm-ics-download",
	templateUrl: "./confirm-ics-download.component.html",
	styleUrls: ["./confirm-ics-download.component.scss"],
	standalone: false
})
export class ConfirmIcsDownloadComponent {
	event: IEvent;
	module: IModule;
	eventUser: IEventUser;
	session: ISchedule;
	allSessions: boolean = false;
	sessions: ISchedule[] = [];
	mode: string;
	language: string;
	appointment: IAppointment;
	isMobile: boolean;
	device: string;
	typeModule = TypeModule;
	icsLoader: boolean;

	constructor(
		private modalCtrl: ModalController,
		private platform: Platform,
		private SSchedule: SchedulesService,
		private SAppointments: AppointmentsService,
		private store: Store,
		private STranslate: TranslateService
	) {
		if (
			(this.platform.is("mobile") && window.innerWidth < 768) ||
			this.platform.is("mobileweb") ||
			window.innerWidth < 768
		) {
			this.isMobile = true;
		} else {
			this.isMobile = false;
		}

		this.device = this.platform.is("android") ? "android" : this.platform.is("ios") ? "ios" : "desktop";
	}

	/**
	 * Generate ics file
	 * @param session
	 * @returns
	 */
	generateTimeSlotIcsFile(appointment: IAppointment) {
		this.icsLoader = true;
		// Implement a loader
		if (appointment) {
			this.SAppointments.generateAppointmentIcsFile(appointment);
			this.icsLoader = false;
		}
	}

	/**
	 * linkToCalendar
	 * @param appointment
	 */
	async linkTimeSlotToCalendar(appointment: IAppointment) {
		this.icsLoader = true;
		let location: string = "";
		if (appointment.location) {
			location = (await firstValueFrom(this.store.select(getSpecificLocation(appointment.location))))?.address;
		}
		const link = this.SAppointments.linkToCalendar(this.event, this.eventUser, location, appointment);
		window.open(link, "_blank");

		this.icsLoader = false;
	}

	/**
	 * addSessionToCalendar - now using native calendar
	 * @param type
	 */
	async addSessionToCalendar(type: string) {
		try {
			this.icsLoader = true;

			if (this.platform.is("capacitor") || this.platform.is("ios") || this.platform.is("android")) {
				// Use native calendar functionality
				try {
					// Request calendar permissions first
					const permissionResult = await CapacitorCalendar.requestFullCalendarAccess();

					if (permissionResult.result !== "granted") {
						console.error("Calendar permission not granted");
						// Fallback to existing method
						this.SSchedule.addSessionToCalendar(this.session, type, this.eventUser, this.language);
						return;
					}

					// Prepare session data
					const sessionTitle =
						typeof this.session.name === "string"
							? this.session.name
							: this.session.name?.[this.language] || this.session.name?.EnUS || "Event Session";
					const sessionStartDate = new Date(this.session.startDate).getTime();
					const sessionEndDate = new Date(this.session.endDate).getTime();

					let location = "";
					if (this.session.locations && this.session.locations.length > 0) {
						const locationData = await firstValueFrom(
							this.store.select(getSpecificLocation(this.session.locations[0]))
						);
						location = locationData?.address || "";
					}

					const sessionDescription =
						typeof this.session.description === "string"
							? this.session.description
							: this.session.description?.[this.language] ||
								this.session.description?.EnUS ||
								`Session from ${this.event.title}`;

					// Open native calendar interface for the session
					const result = await CapacitorCalendar.createEventWithPrompt({
						title: `${this.event.title} - ${sessionTitle}`,
						location: location,
						startDate: sessionStartDate,
						endDate: sessionEndDate,
						description: sessionDescription,
						isAllDay: false
					});

					if (result && result.id) {
						// Close the modal on success
						this.closeModal();
					} else {
						console.warn("Session was not added to calendar");
						// Fallback to existing method
						this.SSchedule.addSessionToCalendar(this.session, type, this.eventUser, this.language);
					}
				} catch (calendarError) {
					console.error("Error with native calendar:", calendarError);
					// Fallback to existing method
					this.SSchedule.addSessionToCalendar(this.session, type, this.eventUser, this.language);
				}
			} else {
				// Web platform - fallback to existing method
				this.SSchedule.addSessionToCalendar(this.session, type, this.eventUser, this.language);
			}
		} catch (error) {
			console.error("Error processing session calendar addition:", error);
			// Handle error - fallback to existing method
			this.SSchedule.addSessionToCalendar(this.session, type, this.eventUser, this.language);
		} finally {
			this.icsLoader = false;
		}
	}

	async addAppointmentToCalendar(appointment: IAppointment) {
		try {
			this.icsLoader = true;

			if (this.platform.is("capacitor") || this.platform.is("ios") || this.platform.is("android")) {
				// Use native calendar functionality
				try {
					// Request calendar permissions first
					const permissionResult = await CapacitorCalendar.requestFullCalendarAccess();

					if (permissionResult.result !== "granted") {
						console.error("Calendar permission not granted");
						// Fallback to existing method
						this.generateTimeSlotIcsFile(appointment);
						return;
					}

					// Prepare appointment data
					const appointmentTitle =
						appointment.appointmentInformations.subject || `Appointment - ${this.event.title}`;
					const appointmentStartDate = new Date(appointment.startDate).getTime();
					const appointmentEndDate =
						new Date(appointment.startDate).getTime() + appointment.duration * 60 * 1000;

					let location = "";
					if (appointment.location) {
						const locationData = await firstValueFrom(
							this.store.select(getSpecificLocation(appointment.location))
						);
						location = locationData?.address || "";
					}

					const appointmentDescription =
						appointment.appointmentInformations.informations || `Appointment from ${this.event.title}`;

					// Open native calendar interface for the appointment
					const result = await CapacitorCalendar.createEventWithPrompt({
						title: appointmentTitle,
						location: location,
						startDate: appointmentStartDate,
						endDate: appointmentEndDate,
						description: appointmentDescription,
						isAllDay: false
					});

					if (result && result.id) {
						// Close the modal on success
						this.closeModal();
					} else {
						console.warn("Appointment was not added to calendar");
						// Fallback to existing method
						this.generateTimeSlotIcsFile(appointment);
					}
				} catch (calendarError) {
					console.error("Error with native calendar:", calendarError);
					// Fallback to existing method
					this.generateTimeSlotIcsFile(appointment);
				}
			} else {
				// Web platform - fallback to existing method
				this.generateTimeSlotIcsFile(appointment);
			}
		} catch (error) {
			console.error("Error processing appointment calendar addition:", error);
			// Handle error - fallback to existing method
			this.generateTimeSlotIcsFile(appointment);
		} finally {
			this.icsLoader = false;
		}
	}

	/**
	 * Download schedule sessions - now using native calendar
	 */
	downloadSchedule(event: Event) {
		// try {
		this.icsLoader = true;
		event.stopPropagation();

		// if (this.platform.is("capacitor") || this.platform.is("ios") || this.platform.is("android")) {
		// 	// Use native calendar functionality
		// 	try {
		// 		// Request calendar permissions first
		// 		const permissionResult = await CapacitorCalendar.requestFullCalendarAccess();

		// 		if (permissionResult.result !== "granted") {
		// 			console.error("Calendar permission not granted");
		// 			// Fallback to ICS file generation
		// 			this.generateIcsFile();
		// 			return;
		// 		}

		// 		// Create events one by one using native prompt
		// 		let successCount = 0;
		// 		for (const session of this.sessions) {
		// 			try {
		// 				const sessionTitle =
		// 					typeof session.name === "string"
		// 						? session.name
		// 						: session.name?.[this.language] || session.name?.EnUS || "Event Session";
		// 				const sessionStartDate = new Date(session.startDate).getTime();
		// 				const sessionEndDate = new Date(session.endDate).getTime();

		// 				let location = "";
		// 				if (session.locations && session.locations.length > 0) {
		// 					const locationData = await firstValueFrom(
		// 						this.store.select(getSpecificLocation(session.locations[0]))
		// 					);
		// 					location = locationData?.address || "";
		// 				}

		// 				const sessionDescription =
		// 					typeof session.description === "string"
		// 						? session.description
		// 						: session.description?.[this.language] ||
		// 							session.description?.EnUS ||
		// 							`Session from ${this.event.title}`;

		// 				// Open native calendar interface for each session
		// 				const result = await CapacitorCalendar.createEventWithPrompt({
		// 					title: `${this.event.title} - ${sessionTitle}`,
		// 					location: location,
		// 					startDate: sessionStartDate,
		// 					endDate: sessionEndDate,
		// 					description: sessionDescription,
		// 					isAllDay: false
		// 				});

		// 				if (result && result.id) {
		// 					successCount++;
		// 				}
		// 			} catch (sessionError) {
		// 				console.error("Error creating individual calendar event:", sessionError);
		// 				// Continue with the next session
		// 			}
		// 		}

		// 		if (successCount > 0) {
		// 			console.log(`Successfully prompted to add ${successCount} events to calendar`);
		// 			// Close the modal on success
		// 			this.closeModal();
		// 		} else {
		// 			console.warn("No events were successfully added");
		// 			// Fallback to ICS file generation
		// 			this.generateIcsFile();
		// 		}
		// 	} catch (calendarError) {
		// 		console.error("Error with native calendar:", calendarError);
		// 		// Fallback to ICS file generation
		// 		this.generateIcsFile();
		// 	}
		// } else {
		// 	// Web platform - fallback to ICS file
		this.generateIcsFile();
		// }
		// } catch (error) {
		// 	console.error("Error processing schedule:", error);
		// 	// Handle error - fallback to ICS generation
		// 	this.generateIcsFile();
		// } finally {
		this.icsLoader = false;
		// }
	}

	/**
	 * Fallback method to generate ICS file (existing logic)
	 */
	private generateIcsFile() {
		const icsData = this.SSchedule.generateSessionsIcsFileFromClient(
			this.module,
			this.sessions,
			this.event.language
		);

		const fileName = `schedule-${this.event.uid}.ics`;
		this.SSchedule.downloadFile(fileName, icsData);
	}

	closeModal() {
		this.modalCtrl.dismiss();
	}
}
